import { ArrowBackOutlined, SearchOutlined, SwapHorizOutlined } from "@mui/icons-material";
import {
    InputAdornment,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { roomPath, roomTypeLabel, type RoomType } from "../room-path";
import type { Room } from "../schema";
import { chrome } from "../theme/tokens";
import { iconSx, labelProps, menuItemSx } from "./nav-styles";

// Staff-only room switcher (2026-11-02, Elijah). "Switch room" sits above the
// signed-in user; a click swaps the drawer's page list for this panel in
// place (no popup), on desktop and inside the phone drawer alike. Empty
// search lists the last rooms this browser opened; typing searches every room
// of the same type from the staff directory (GET /api/rooms). Never crosses
// types: a deal room switches to deal rooms. A switch keeps the page: from the
// checklist you land on the other room's checklist; RoomPage falls back to the
// page of the same template, then to the room's Home (Elijah, 2026-11-02).
// ponytail: recents live in localStorage, per browser; move to the event log
// if staff want them across machines.

const RECENT_KEY = "deal-room.recent-rooms.v1";
const RECENT_SHOWN = 5;
const RECENT_KEPT = 30;
const SEARCH_SHOWN = 12;

interface RoomRef {
    slug: string;
    roomType: RoomType;
    name: string;
}

function readRecent(): RoomRef[] {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? "[]");
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function rememberRoom(ref: RoomRef) {
    try {
        const rest = readRecent().filter((r) => r.slug !== ref.slug);
        window.localStorage.setItem(RECENT_KEY, JSON.stringify([ref, ...rest].slice(0, RECENT_KEPT)));
    } catch {
        /* private mode: no recents */
    }
}

interface DirectoryRow {
    slug: string;
    roomType: string;
    supplierName?: string;
}

/** The footer row that opens the panel; mounting it also records this visit. */
export function SwitchRoomButton({
    room,
    expanded,
    active,
    onClick,
}: {
    room: Room;
    expanded: boolean;
    active: boolean;
    onClick: () => void;
}) {
    const type = room.roomType;
    const name = room.supplier.supplierName;
    useEffect(() => {
        rememberRoom({ slug: room.slug, roomType: type, name });
    }, [room.slug, type, name]);

    const row = (
        <MenuItem component="button" onClick={onClick} selected={active} aria-expanded={active} sx={{ ...menuItemSx(active, expanded), textAlign: "left" }}>
            <ListItemIcon sx={{ ...iconSx, color: active ? chrome.ink : chrome.iconPrimary }}>
                <SwapHorizOutlined />
            </ListItemIcon>
            {expanded && <ListItemText primary="Switch room" primaryTypographyProps={labelProps(active)} />}
        </MenuItem>
    );
    return expanded ? row : <Tooltip title="Switch room" placement="right">{row}</Tooltip>;
}

/** Shown in place of the page list while switching. */
/** What the switch carries to the other room so it can open the same page. */
export interface RoomSwitchState {
    switchedFromTemplate: string;
}

export function RoomSwitcherPanel({
    room,
    currentPage,
    onClose,
    onNavigate,
}: {
    room: Room;
    /** The page being viewed; absent or Home means the other room's Home. */
    currentPage?: { slug: string; template: string; isHome: boolean };
    onClose: () => void;
    onNavigate: () => void;
}) {
    const [query, setQuery] = useState("");
    const type = room.roomType;
    const q = query.trim().toLowerCase();

    const directory = useQuery({
        queryKey: ["rooms-directory"],
        enabled: q !== "",
        staleTime: 5 * 60_000,
        queryFn: async (): Promise<DirectoryRow[]> => {
            const res = await fetch("/api/rooms", { credentials: "include" });
            if (!res.ok) throw new Error(`rooms list ${res.status}`);
            return ((await res.json()) as { rooms: DirectoryRow[] }).rooms;
        },
    });

    const items: RoomRef[] = !q
        ? readRecent()
              .filter((r) => r.roomType === type && r.slug !== room.slug)
              .slice(0, RECENT_SHOWN)
        : (directory.data ?? [])
              .filter((r) => r.roomType === type && r.slug !== room.slug)
              .map((r) => ({ slug: r.slug, roomType: type, name: r.supplierName ?? r.slug }))
              .filter((r) => r.name.toLowerCase().includes(q) || r.slug.includes(q))
              .sort((x, y) => x.name.localeCompare(y.name))
              .slice(0, SEARCH_SHOWN);

    const typeLabel = roomTypeLabel(type);
    let empty = "";
    if (!q) empty = `No other ${typeLabel}s opened in this browser yet. Search to find one.`;
    else if (directory.isLoading) empty = "Searching...";
    else if (directory.isError) empty = "The room list did not load. Try again in a moment.";
    else if (items.length === 0) empty = `No ${typeLabel} matches "${query.trim()}".`;

    const hint = { fontSize: 12, fontWeight: 600, color: chrome.inkBody, px: 1, mt: 1.5, mb: 0.5 } as const;

    return (
        <List component="div" sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", mx: -1.5, px: 1.5, py: 1 }}>
            <ListItem disablePadding>
                <MenuItem component="button" onClick={onClose} sx={{ ...menuItemSx(false, true), textAlign: "left" }}>
                    <ListItemIcon sx={iconSx}>
                        <ArrowBackOutlined />
                    </ListItemIcon>
                    <ListItemText
                        primary={`Back to ${room.supplier.supplierName}`}
                        primaryTypographyProps={labelProps(false)}
                    />
                </MenuItem>
            </ListItem>
            <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder={`Search ${typeLabel}s`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Escape") onClose();
                }}
                sx={{ mt: 1, "& input": { fontSize: 13 } }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlined sx={{ fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <Typography sx={hint}>{q ? "Results" : "Recent"}</Typography>
            {items.length === 0 ? (
                <Typography sx={{ fontSize: 13, lineHeight: "20px", color: chrome.inkBody, px: 1, py: 0.5 }}>
                    {empty}
                </Typography>
            ) : (
                items.map((r) => (
                    <ListItem key={r.slug} disablePadding>
                        <MenuItem
                            component={RouterLink}
                            to={
                                currentPage && !currentPage.isHome
                                    ? `${roomPath(r.slug, false, r.roomType)}/${currentPage.slug}`
                                    : roomPath(r.slug, false, r.roomType)
                            }
                            state={currentPage && !currentPage.isHome ? ({ switchedFromTemplate: currentPage.template } satisfies RoomSwitchState) : undefined}
                            onClick={() => {
                                onClose();
                                onNavigate();
                            }}
                            sx={menuItemSx(false, true)}
                        >
                            <ListItemText primary={r.name} primaryTypographyProps={labelProps(false)} />
                        </MenuItem>
                    </ListItem>
                ))
            )}
        </List>
    );
}
